import { orderBy } from 'lodash';

import { HIGHLIGHT_MARK, type HighlightMarkType } from 'src/data/markedText';
import { COLORS } from 'src/data/ui';
import type { Entity } from 'src/lib/collection';

export interface BrandTerm extends Entity {
  term: string;
  approvalType: ApprovalType;
  status: BrandTermStatus;
  updatedAt: string;
  replaceWith?: string;
  partOfSpeech?: string;
  caseSensitive?: boolean;
  capitalizeAtStart?: boolean;
  consistentHyphenation?: boolean;
  commonMistakes?: string[];
  description?: string;
  tags?: string[];
}

export interface DetectedBrandTerm {
  brandTerm: BrandTerm;
  termInText: string;
  termStartPosition: number;
  termEndPosition: number;
  replacement?: string;
  reason?: DetectedBrandTermReason;
}

/**
 * Brand term approval type
 */
export const APPROVAL_TYPE = {
  approved: 'approved',
  doNotUse: 'dont_use',
  useCarefully: 'use_carefully',
} as const;
export type ApprovalType = (typeof APPROVAL_TYPE)[keyof typeof APPROVAL_TYPE];

const APPROVAL_TYPE_PROPS = {
  [APPROVAL_TYPE.approved]: {
    title: 'Approved',
    description: 'Terms that are ok to use',
  },
  [APPROVAL_TYPE.doNotUse]: {
    title: 'Don’t use',
    description: 'Terms that will not be included in the generated text',
  },
  [APPROVAL_TYPE.useCarefully]: {
    title: 'Use carefully',
    description: 'Terms that will be generated but also flagged',
  },
};

export const getApprovalTypeTitle = (type: ApprovalType) => APPROVAL_TYPE_PROPS[type]?.title;
export const getApprovalTypeDescription = (type: ApprovalType) =>
  APPROVAL_TYPE_PROPS[type]?.description;

/**
 * Brand term status
 */
export const BRAND_TERM_STATUS = {
  active: 'active',
  inactive: 'inactive',
  discarded: 'discarded',
} as const;
export type BrandTermStatus = (typeof BRAND_TERM_STATUS)[keyof typeof BRAND_TERM_STATUS];

export const isBrandTermActive = (brandTerm: BrandTerm) =>
  brandTerm?.status === BRAND_TERM_STATUS.active;
export const isBrandTermInactive = (brandTerm: BrandTerm) =>
  brandTerm?.status === BRAND_TERM_STATUS.inactive;

/**
 * Detected brand term type
 */
export const DETECTED_BRAND_TERM_TYPE = {
  approved: 'approved',
  approvedMistake: 'approved_mistake',
  approvedCaseSensitive: 'approved_case_sensitive',
  approvedHyphenationConsistency: 'approved_hyphenation_consistency',
  doNotUse: 'dont_use',
  useCarefully: 'use_carefully',
} as const;
export type DetectedBrandTermType =
  (typeof DETECTED_BRAND_TERM_TYPE)[keyof typeof DETECTED_BRAND_TERM_TYPE];

const DETECTED_BRAND_TERM_TYPE_PROPS = {
  [DETECTED_BRAND_TERM_TYPE.approved]: {
    title: 'Approved term',
    colorGroup: COLORS.success,
    highlightMark: HIGHLIGHT_MARK.positive,
  },
  [DETECTED_BRAND_TERM_TYPE.approvedMistake]: {
    title: 'Common mistake',
    colorGroup: COLORS.warning,
    highlightMark: HIGHLIGHT_MARK.warning,
  },
  [DETECTED_BRAND_TERM_TYPE.approvedCaseSensitive]: {
    title: 'Capitalization mistake',
    colorGroup: COLORS.warning,
    highlightMark: HIGHLIGHT_MARK.warning,
  },
  [DETECTED_BRAND_TERM_TYPE.approvedHyphenationConsistency]: {
    title: 'Hyphenation mistake',
    colorGroup: COLORS.warning,
    highlightMark: HIGHLIGHT_MARK.warning,
  },
  [DETECTED_BRAND_TERM_TYPE.doNotUse]: {
    title: 'Don’t use',
    colorGroup: COLORS.error,
    highlightMark: HIGHLIGHT_MARK.error,
  },
  [DETECTED_BRAND_TERM_TYPE.useCarefully]: {
    title: 'Use carefully',
    colorGroup: COLORS.warning,
    highlightMark: HIGHLIGHT_MARK.warning,
  },
};

export const getDetectedBrandTermTypeTitle = (type: DetectedBrandTermType): string =>
  DETECTED_BRAND_TERM_TYPE_PROPS[type]?.title;
export const getDetectedBrandTermTypeColorGroup = (type: DetectedBrandTermType) =>
  DETECTED_BRAND_TERM_TYPE_PROPS[type]?.colorGroup;
export const getDetectedBrandTermTypeHighlightMark = (
  type: DetectedBrandTermType
): HighlightMarkType => DETECTED_BRAND_TERM_TYPE_PROPS[type]?.highlightMark;

/**
 * Detected brand term reason
 */
export const DETECTED_BRAND_TERM_REASON = {
  approved: 'approved',
  mistake: 'mistake',
  caseSensitive: 'case_sensitive',
  hyphenationConsistency: 'hyphenation_consistency',
} as const;
export type DetectedBrandTermReason =
  (typeof DETECTED_BRAND_TERM_REASON)[keyof typeof DETECTED_BRAND_TERM_REASON];

/**
 * Get detected brand term type based on the approval type and reason
 * @param detectedTerm - Detected brand term
 */
export const getDetectedBrandTermType = (
  detectedTerm: DetectedBrandTerm
): DetectedBrandTermType | undefined => {
  const approvalType = detectedTerm?.brandTerm?.approvalType;
  if (!approvalType) {
    return undefined;
  }

  if (approvalType === APPROVAL_TYPE.approved) {
    if (!detectedTerm.reason) {
      return DETECTED_BRAND_TERM_TYPE.approved;
    }

    const REASON_TO_DETECTED_TYPE = {
      [DETECTED_BRAND_TERM_REASON.approved]: DETECTED_BRAND_TERM_TYPE.approved,
      [DETECTED_BRAND_TERM_REASON.mistake]: DETECTED_BRAND_TERM_TYPE.approvedMistake,
      [DETECTED_BRAND_TERM_REASON.caseSensitive]: DETECTED_BRAND_TERM_TYPE.approvedCaseSensitive,
      [DETECTED_BRAND_TERM_REASON.hyphenationConsistency]:
        DETECTED_BRAND_TERM_TYPE.approvedHyphenationConsistency,
    };
    return REASON_TO_DETECTED_TYPE[detectedTerm.reason];
  }

  const APPROVAL_TO_DETECTED_TYPE = {
    [APPROVAL_TYPE.doNotUse]: DETECTED_BRAND_TERM_TYPE.doNotUse,
    [APPROVAL_TYPE.useCarefully]: DETECTED_BRAND_TERM_TYPE.useCarefully,
  };
  return APPROVAL_TO_DETECTED_TYPE[approvalType];
};

export const brandTermSupportsReplacement = (approvalType: ApprovalType) => {
  const array: ApprovalType[] = [APPROVAL_TYPE.doNotUse, APPROVAL_TYPE.useCarefully];
  return array.includes(approvalType);
};

export const sortBrandTerms = (brandTerms: BrandTerm[]) =>
  orderBy(brandTerms, ['updatedAt', 'term', 'id'], ['desc', 'asc', 'asc']);

export const RELOAD_BRAND_RULES_TOKEN_FIELD = 'postMaloneBrandRulesReload';

/**
 * Brand term part of speech
 */
export const PART_OF_SPEECH = {
  noun: 'noun',
  adjective: 'adj',
  verb: 'verb',
  adverb: 'adv',
} as const;
export type PartOfSpeech = (typeof PART_OF_SPEECH)[keyof typeof PART_OF_SPEECH];

export const UNKNOWN_PART_OF_SPEECH = 'unknown';

const PART_OF_SPEECH_PROPS = {
  [PART_OF_SPEECH.noun]: {
    title: 'Noun',
  },
  [PART_OF_SPEECH.adjective]: {
    title: 'Adjective',
  },
  [PART_OF_SPEECH.verb]: {
    title: 'Verb',
  },
  [PART_OF_SPEECH.adverb]: {
    title: 'Adverb',
  },
};

export const getPartOfSpeechTitle = (type: PartOfSpeech) => {
  if (!isPartOfSpeechValid(type)) {
    return 'None';
  }
  return PART_OF_SPEECH_PROPS[type]?.title;
};

export const isPartOfSpeechValid = (type: string): type is PartOfSpeech =>
  Object.values(PART_OF_SPEECH).includes(type as PartOfSpeech);

export const createEditorTextChangeFromDetectedBrandTerm = (detectedTerm: DetectedBrandTerm) => {
  if (!detectedBrandTermHasReplacement(detectedTerm)) {
    return null;
  }

  const { termInText, termStartPosition, termEndPosition, replacement } = detectedTerm;
  return {
    startIndex: termStartPosition,
    endIndex: termEndPosition,
    oldText: termInText,
    newText: replacement,
  };
};

export const detectedBrandTermIsCommonMistake = (detectedTerm: DetectedBrandTerm) => {
  if (detectedTerm.brandTerm.approvalType !== APPROVAL_TYPE.approved) {
    return false;
  }

  return typeof detectedTerm.replacement === 'string' ? detectedTerm.replacement.length > 0 : false;
};

export const detectedBrandTermHasReplacement = (detectedTerm: DetectedBrandTerm) => {
  if (detectedBrandTermIsCommonMistake(detectedTerm)) {
    return true;
  }
  return typeof detectedTerm.replacement === 'string' ? detectedTerm.replacement.length > 0 : false;
};

export const brandTermToEventAttributes = (term: BrandTerm) => ({
  brandTermId: term.id,
  term: term.term,
  termType: term.approvalType,
  caseSensitive: term.caseSensitive,
  hyphenationConsistency: term.consistentHyphenation,
  termsToCorrect: term.commonMistakes?.join(', '),
  replaceWith: term.replaceWith,
  partOfSpeech: term.partOfSpeech,
});
